import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { getTranslations } from '@assets/i18n/config';
import { CONFIG } from '@config';
import { getLanguageDefaults } from '@onevesthq/ov-translations';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { UserContext } from '@providers/userContextProvider';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AssignPortfolioToSubAccountVisual } from './assignPortfolioToSubAccount.visual';
import {
  determineUserSuitability,
  FETCH_GOAL,
  FETCH_GOAL_WITH_SUBACCOUNTS,
  FETCH_HOUSEHOLD_SUITABILITY,
  FETCH_MODEL_PORTFOLIOS,
  FETCH_SUB_ACCOUNT,
  FETCH_THEMES,
  FETCH_USER_SUITABILITY,
  type PortfolioDataTransformConfig,
  SUGGEST_PORTFOLIO,
  type SubAccountPortfolioData,
  suitabilityScore,
  transformGoalPortfolioData,
  transformMultiSubAccountPortfolioData,
  transformSingleSubAccountPortfolioData,
  UPDATE_GOAL,
  UPDATE_SUB_ACCOUNT,
} from './utils';

const MOCK_CUSTODIAN_SUITABILITY_SCORE = 10;

export const AssignPortfolioToSubAccount = ({
  options,
  stepLoading,
  workflowCompletion,
  objectId,
  objectType = 'USER',
  onNext,
}: {
  options: any;
  objectId: string;
  objectType?: string;
  onNext: () => void;
  stepLoading: boolean;
  workflowCompletion?: any;
}) => {
  const { activeOrganization } = useContext(UserContext);
  const { workflowData } = useContext(WorkflowContext);

  // Unified state - always an array (length 1 for single mode, N for multi mode)
  const [subAccountsData, setSubAccountsData] = useState<
    SubAccountPortfolioData[]
  >([]);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [lockThemeSelection, setLockThemeSelection] = useState<boolean>(false);
  const [noSuggestion, setNoSuggestion] = useState<boolean>(false);

  // Options
  const setPortfolioOnGoal =
    options.setPortfolioOnGoal && activeOrganization?.allowPortfolioPerGoal;
  const isCreateSubAccountForAccountHardcodedWorkflow =
    workflowCompletion.id === 'create-sub-account-workflow' &&
    workflowData.currentGoalId;
  const manuallyAssignModelPortfolios = options?.manuallyAssignModelPortfolios;
  const enableMultiSubAccountMode = options?.enableMultiSubAccountMode || false;
  const useExternalSuitabilityScore =
    options.useExternalSuitabilityScore && CONFIG.app.env !== 'production';

  // Computed values
  const currentSubAccount = subAccountsData[activeIndex];
  const showTabs = enableMultiSubAccountMode && subAccountsData.length > 1;

  // Helper functions for state management
  const updateSubAccountData = useCallback(
    (index: number, updates: Partial<SubAccountPortfolioData>) => {
      setSubAccountsData((prev) =>
        prev.map((data, i) => (i === index ? { ...data, ...updates } : data)),
      );
    },
    [],
  );

  const updateCurrentSubAccount = useCallback(
    (updates: Partial<SubAccountPortfolioData>) => {
      updateSubAccountData(activeIndex, updates);
    },
    [activeIndex, updateSubAccountData],
  );

  // GraphQL queries and mutations
  const { data, loading: fetchSubAccountLoading } = useQuery(
    FETCH_SUB_ACCOUNT,
    {
      variables: { subAccountId: workflowData.currentSubAccountId },
      skip:
        setPortfolioOnGoal ||
        isCreateSubAccountForAccountHardcodedWorkflow ||
        enableMultiSubAccountMode,
      fetchPolicy: 'no-cache',
    },
  );

  const [
    fetchSubAccountLazy,
    { data: lazySubAccountData, loading: lazySubAccountLoading },
  ] = useLazyQuery(FETCH_SUB_ACCOUNT, {
    fetchPolicy: 'no-cache',
  });

  const { data: goalData, loading: fetchGoalLoading } = useQuery(
    enableMultiSubAccountMode ? FETCH_GOAL_WITH_SUBACCOUNTS : FETCH_GOAL,
    {
      variables: { goalId: workflowData.currentGoalId },
      skip:
        !setPortfolioOnGoal &&
        !isCreateSubAccountForAccountHardcodedWorkflow &&
        !enableMultiSubAccountMode,
      fetchPolicy: 'no-cache',
    },
  );

  const { loading: allThemesLoading, data: allThemesData } = useQuery(
    FETCH_THEMES,
    {
      variables: {
        input: {
          filter: {
            organizationId: activeOrganization.id,
            state: 'ACTIVE',
          },
        },
      },
      skip: !manuallyAssignModelPortfolios,
    },
  );

  const { loading: userLoading, data: userSuitabilityData } = useQuery(
    objectType === 'HOUSEHOLD'
      ? FETCH_HOUSEHOLD_SUITABILITY
      : FETCH_USER_SUITABILITY,
    {
      variables: {
        ...(objectType === 'HOUSEHOLD'
          ? { clientGroupId: objectId }
          : { userId: objectId }),
      },
    },
  );

  const [suggestPortfolio, { loading: suggestPortfolioLoading }] =
    useLazyQuery(SUGGEST_PORTFOLIO);
  const [fetchModelPortfolios, { loading: modelPortfolioLoading }] =
    useLazyQuery(FETCH_MODEL_PORTFOLIOS);
  const [updateSubAccount, { loading: updateSubAccountLoading }] =
    useMutation(UPDATE_SUB_ACCOUNT);
  const [updateGoal, { loading: updateGoalLoading }] = useMutation(UPDATE_GOAL);

  // Fetch model portfolios for a specific theme
  const fetchModelPortfoliosForTheme = useCallback(
    async (themeId: string): Promise<any[]> => {
      const result = await fetchModelPortfolios({
        variables: {
          input: {
            filter: {
              organizationId: activeOrganization.id,
              themeId,
              state: 'ACTIVE',
            },
            pagination: { perPage: 100 },
          },
        },
        fetchPolicy: 'no-cache',
      });
      return result.data?.fetchModelPortfolios?.modelPortfolios || [];
    },
    [fetchModelPortfolios, activeOrganization.id],
  );

  // Suggest portfolio for a specific subaccount
  const suggestPortfolioForSubAccount = useCallback(
    async (
      index: number,
      subAccountData: SubAccountPortfolioData,
    ): Promise<void> => {
      if (!subAccountData?.userSuitability || !subAccountData?.themeId) return;

      const result = await suggestPortfolio({
        variables: {
          suitabilityScore: suitabilityScore(
            subAccountData,
            useExternalSuitabilityScore,
            options,
          ),
          organizationId: activeOrganization.id,
          themeId: subAccountData.themeId,
        },
        fetchPolicy: 'network-only',
      });

      if (result.data) {
        const suggestedProduct =
          result.data.suggestFinancialProduct.financialProduct;
        const suggestionMatchesTheme =
          suggestedProduct.theme.id === subAccountData.themeId;

        // Update the subaccount data with the suggestion
        updateSubAccountData(index, {
          suggestedFinancialProduct: suggestedProduct,
          // Set the suggested product based on manual selection mode:
          // - If manual selection is OFF: always use suggested product (if theme matches)
          // - If manual selection is ON: only use suggested product if no current selection
          financialProduct:
            suggestionMatchesTheme &&
            (!manuallyAssignModelPortfolios || !subAccountData.financialProduct)
              ? suggestedProduct
              : subAccountData.financialProduct,
        });

        if (!suggestionMatchesTheme) setNoSuggestion(true);
      }
    },
    [
      useExternalSuitabilityScore,
      suggestPortfolio,
      activeOrganization.id,
      manuallyAssignModelPortfolios,
      updateSubAccountData,
      setNoSuggestion,
      options.enableMultiSubAccountMode,
    ],
  );

  // Initialize unified subaccount portfolio data array
  useEffect(() => {
    const userSuitability = determineUserSuitability(
      objectType,
      userSuitabilityData,
    );

    // Create configuration object for portfolio data transformation
    const config: PortfolioDataTransformConfig = {
      manuallyAssignModelPortfolios,
      userSuitability,
      custodianSuitabilityScore: MOCK_CUSTODIAN_SUITABILITY_SCORE,
      currentAccountId: workflowData.currentAccountId || '',
      allThemesData,
      isCreateSubAccountForAccountHardcodedWorkflow:
        !!isCreateSubAccountForAccountHardcodedWorkflow,
    };

    if (enableMultiSubAccountMode && goalData?.fetchGoal?.goal?.subAccounts) {
      // Multi-subaccount mode: use goal subaccounts
      const portfolioData = transformMultiSubAccountPortfolioData(
        goalData.fetchGoal.goal.subAccounts,
        config,
      );
      setSubAccountsData(portfolioData);
    } else if (!enableMultiSubAccountMode) {
      // Single subaccount mode: use current subaccount or goal
      let portfolioData: SubAccountPortfolioData[] = [];

      if (data?.fetchSubAccount?.subAccount) {
        // Use fetched subaccount
        const { subAccount } = data.fetchSubAccount;
        portfolioData = transformSingleSubAccountPortfolioData(
          subAccount,
          config,
        );
        // Override the ID and name for current subaccount
        portfolioData[0].id = workflowData.currentSubAccountId || '';
        portfolioData[0].name = 'Current SubAccount';
      } else if (goalData?.fetchGoal?.goal) {
        // Try to use goal data first
        const { goal } = goalData.fetchGoal;
        portfolioData = transformGoalPortfolioData(
          goal,
          config,
          workflowData.currentGoalId || '',
        );

        // If transformGoalPortfolioData returns empty (hardcoded workflow with no goal themeId),
        // we need to fetch subAccount data as fallback
        if (
          portfolioData.length === 0 &&
          !lazySubAccountData &&
          !lazySubAccountLoading
        ) {
          fetchSubAccountLazy({
            variables: { subAccountId: workflowData.currentSubAccountId },
          });
          return; // Wait for lazy query to complete
        }

        // Use lazy subAccount data for fallback if available
        if (
          portfolioData.length === 0 &&
          lazySubAccountData?.fetchSubAccount?.subAccount
        ) {
          const { subAccount } = lazySubAccountData.fetchSubAccount;
          portfolioData = transformSingleSubAccountPortfolioData(
            subAccount,
            config,
          );
          // Override the ID and name for current subaccount
          portfolioData[0].id = workflowData.currentSubAccountId || '';
          portfolioData[0].name = 'Current SubAccount';
        }
      }

      if (portfolioData.length > 0) {
        setSubAccountsData(portfolioData);
      }
    }
  }, [
    enableMultiSubAccountMode,
    goalData,
    data,
    lazySubAccountData,
    lazySubAccountLoading,
    userSuitabilityData,
    allThemesData,
    manuallyAssignModelPortfolios,
    objectType,
    workflowData.currentSubAccountId,
    workflowData.currentGoalId,
    workflowData.currentAccountId,
    isCreateSubAccountForAccountHardcodedWorkflow,
    fetchSubAccountLazy,
  ]);

  // Fetch model portfolios when theme changes for manual assignment
  useEffect(() => {
    if (
      !currentSubAccount?.availableThemes?.find(
        (theme) => theme.id === currentSubAccount?.themeId,
      )
    ) {
      updateCurrentSubAccount({ ...currentSubAccount, themeId: null });
    } else if (
      manuallyAssignModelPortfolios &&
      currentSubAccount?.themeId &&
      (!currentSubAccount.availableFinancialProducts ||
        currentSubAccount.availableFinancialProducts.length === 0)
    ) {
      fetchModelPortfoliosForTheme(currentSubAccount.themeId).then(
        (availableProducts) => {
          updateCurrentSubAccount({
            availableFinancialProducts: availableProducts,
          });
          // Auto-suggest portfolio after loading model portfolios
          if (
            availableProducts.length > 0 &&
            currentSubAccount.userSuitability
          ) {
            suggestPortfolioForSubAccount(activeIndex, {
              ...currentSubAccount,
              availableFinancialProducts: availableProducts,
            });
          }
        },
      );
    }
  }, [
    manuallyAssignModelPortfolios,
    currentSubAccount?.themeId,
    currentSubAccount?.availableFinancialProducts,
    currentSubAccount?.userSuitability,
    currentSubAccount,
    fetchModelPortfoliosForTheme,
    updateCurrentSubAccount,
    suggestPortfolioForSubAccount,
    activeIndex,
  ]);

  // Auto-suggest portfolios when data is ready
  useEffect(() => {
    if (subAccountsData.length > 0) {
      // Suggest portfolios for all subaccounts that need suggestions
      subAccountsData.forEach((subAccountData, index) => {
        if (
          subAccountData.themeId &&
          subAccountData.userSuitability &&
          !subAccountData.suggestedFinancialProduct
        ) {
          suggestPortfolioForSubAccount(index, subAccountData);
        }
      });
    }
  }, [subAccountsData, suggestPortfolioForSubAccount]);

  // Set theme locking when needed
  useEffect(() => {
    if (
      isCreateSubAccountForAccountHardcodedWorkflow &&
      goalData?.fetchGoal?.goal?.financialProduct?.theme?.id
    ) {
      setLockThemeSelection(true);
    }
  }, [
    isCreateSubAccountForAccountHardcodedWorkflow,
    goalData?.fetchGoal?.goal?.financialProduct?.theme?.id,
  ]);

  // Handle lowest suggestion fallback for manual portfolios when no suitability
  useEffect(() => {
    if (
      currentSubAccount?.availableFinancialProducts?.length &&
      (!currentSubAccount.userSuitability || noSuggestion) &&
      !currentSubAccount.financialProduct
    ) {
      const lowestSuggestion =
        currentSubAccount.availableFinancialProducts.reduce(
          (lowest: any, modelPortfolio: any) =>
            modelPortfolio.riskReturnLevel < lowest.riskReturnLevel
              ? modelPortfolio
              : lowest,
          currentSubAccount.availableFinancialProducts[0],
        );

      updateCurrentSubAccount({ financialProduct: lowestSuggestion });
      setNoSuggestion(false);
    }
  }, [
    currentSubAccount?.availableFinancialProducts,
    currentSubAccount?.userSuitability,
    currentSubAccount?.financialProduct,
    noSuggestion,
    updateCurrentSubAccount,
  ]);

  // Handle theme changes
  const handleThemeChange = useCallback(
    (newThemeId: string) => {
      updateCurrentSubAccount({
        themeId: newThemeId,
        financialProduct: null, // Reset financial product when theme changes
      });

      // Fetch new model portfolios for manual assignment and auto-suggest
      if (manuallyAssignModelPortfolios) {
        fetchModelPortfoliosForTheme(newThemeId).then((availableProducts) => {
          updateCurrentSubAccount({
            availableFinancialProducts: availableProducts,
          });
          // Auto-suggest portfolio after loading model portfolios for new theme
          if (
            availableProducts.length > 0 &&
            currentSubAccount?.userSuitability
          ) {
            const updatedSubAccount = {
              ...currentSubAccount,
              themeId: newThemeId,
              availableFinancialProducts: availableProducts,
              financialProduct: null, // Reset to allow new suggestion
            };
            suggestPortfolioForSubAccount(activeIndex, updatedSubAccount);
          }
        });
      } else if (currentSubAccount) {
        // Re-suggest portfolio for new theme (automatic mode)
        const updatedSubAccount = {
          ...currentSubAccount,
          themeId: newThemeId,
          financialProduct: null,
        };
        suggestPortfolioForSubAccount(activeIndex, updatedSubAccount);
      }
    },
    [
      updateCurrentSubAccount,
      manuallyAssignModelPortfolios,
      fetchModelPortfoliosForTheme,
      suggestPortfolioForSubAccount,
      activeIndex,
      currentSubAccount,
    ],
  );

  // Handle financial product selection (for manual mode)
  const handleFinancialProductChange = useCallback(
    (productId: string) => {
      const selectedProduct =
        currentSubAccount?.availableFinancialProducts?.find(
          (fp: any) => fp.id === productId,
        );
      if (selectedProduct) {
        updateCurrentSubAccount({ financialProduct: selectedProduct });
      }
    },
    [currentSubAccount?.availableFinancialProducts, updateCurrentSubAccount],
  );

  // Computed visual data
  const visualData = useMemo(() => {
    if (!currentSubAccount) {
      return {
        themeId: null,
        financialProduct: null,
        availableThemes: [],
        availableFinancialProducts: [],
        userSuitability: null,
      };
    }

    return {
      themeId: currentSubAccount.themeId,
      financialProduct: currentSubAccount.financialProduct,
      availableThemes: currentSubAccount.availableThemes,
      availableFinancialProducts: currentSubAccount.availableFinancialProducts,
      userSuitability: currentSubAccount.userSuitability,
    };
  }, [currentSubAccount]);

  // Tab data for visual component
  const tabsData = useMemo(() => {
    if (!showTabs) return [];

    return subAccountsData.map((subAccount) => ({
      label: subAccount.name
        ? getLanguageDefaults(subAccount.name)
        : getTranslations({
            fileName: 'client',
            translationPath: ['subAccountWithSuffix'],
            variables: { suffix: subAccount.id.slice(-4) },
          }),
      icon: 'account_balance_wallet',
      widgets: [],
    }));
  }, [showTabs, subAccountsData]);

  // Save portfolios
  const setPortfolio = async () => {
    if (!currentSubAccount?.themeId || !currentSubAccount?.financialProduct)
      return;

    if (setPortfolioOnGoal) {
      // Update goal with portfolio
      if (workflowData.currentGoalId) {
        await updateGoal({
          variables: {
            input: {
              goalId: workflowData.currentGoalId,
              financialProductId: manuallyAssignModelPortfolios
                ? currentSubAccount.financialProduct.id
                : currentSubAccount.suggestedFinancialProduct?.id,
            },
          },
        });
      }

      // Update all subaccounts to skip IPS
      if (workflowData.currentSubAccountId) {
        await updateSubAccount({
          variables: {
            input: {
              subAccountId: workflowData.currentSubAccountId,
              skipIPS: true,
            },
          },
        });
      }
    } else {
      // Update individual subaccounts with their portfolios
      const shouldSkipIPS = false; // Normal subAccount updates don't skip IPS

      if (enableMultiSubAccountMode) {
        // Multi-subaccount mode: update all subaccounts
        const updatePromises = subAccountsData.map((subAccountData) => {
          if (!subAccountData.themeId) return Promise.resolve();

          const financialProduct = manuallyAssignModelPortfolios
            ? subAccountData.financialProduct
            : subAccountData.suggestedFinancialProduct;

          if (!financialProduct) return Promise.resolve();

          return updateSubAccount({
            variables: {
              input: {
                subAccountId: subAccountData.id,
                themeId: subAccountData.themeId,
                financialProductId: shouldSkipIPS
                  ? undefined
                  : financialProduct.id,
                skipIPS: shouldSkipIPS || false,
              },
            },
          });
        });

        await Promise.all(updatePromises.filter(Boolean));
      } else if (currentSubAccount.isFromGoal) {
        // Hardcoded workflow with goal themeId: just skip IPS on subaccount
        if (workflowData.currentSubAccountId) {
          await updateSubAccount({
            variables: {
              input: {
                subAccountId: workflowData.currentSubAccountId,
                skipIPS: true,
              },
            },
          });
        }
      } else {
        // Regular subaccount update
        await updateSubAccount({
          variables: {
            input: {
              subAccountId: currentSubAccount.id,
              themeId: currentSubAccount.themeId,
              financialProductId: shouldSkipIPS
                ? undefined
                : manuallyAssignModelPortfolios
                  ? currentSubAccount.financialProduct.id
                  : currentSubAccount.suggestedFinancialProduct?.id,
              skipIPS: shouldSkipIPS || false,
            },
          },
        });
      }
    }

    await onNext();
  };

  const isLoading =
    fetchSubAccountLoading ||
    lazySubAccountLoading ||
    stepLoading ||
    fetchGoalLoading ||
    modelPortfolioLoading ||
    suggestPortfolioLoading ||
    allThemesLoading ||
    userLoading ||
    updateSubAccountLoading ||
    updateGoalLoading;

  return (
    <AssignPortfolioToSubAccountVisual
      options={options}
      objectData={visualData}
      suggestedFinancialProduct={currentSubAccount?.suggestedFinancialProduct}
      updateObject={{
        handleThemeChange,
        handleFinancialProductChange,
      }}
      continueFunc={setPortfolio}
      loading={isLoading}
      workflowCompletion={workflowCompletion}
      lockThemeSelection={lockThemeSelection}
      manuallyAssignModelPortfolios={manuallyAssignModelPortfolios}
      showTabs={showTabs}
      tabsData={tabsData}
      activeSubAccountIndex={activeIndex}
      setActiveSubAccountIndex={setActiveIndex}
    />
  );
};

export default AssignPortfolioToSubAccount;
